import Add from '@mui/icons-material/Add';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { NAVY, RADIUS_ITEM } from 'styles/tokens';

import type { PlaygroundTurn, TurnRole } from '../types';
import { rawJsonToTurns, turnsToRawJson } from '../utils/turnMapping';
import Composer from './Composer';
import RawJsonPanel from './RawJsonPanel';
import TurnBubble from './TurnBubble';

interface ConversationThreadProps {
  turns: PlaygroundTurn[];
  rawOpen: boolean;
  regeneratingUid: string | null;
  /** When true, show the live-chat composer (single mode); compare mode crafts input manually. */
  showComposer: boolean;
  chatSending: boolean;
  sendDisabled: boolean;
  onSendChat: (text: string) => void;
  onUpdateTurn: (uid: string, patch: Partial<PlaygroundTurn>) => void;
  onDeleteTurn: (uid: string) => void;
  onAddTurn: (role: TurnRole) => void;
  onRegenerate: (uid: string) => void;
  onToggleRaw: (open: boolean) => void;
  onSetTurns: (turns: PlaygroundTurn[]) => void;
  onConversationError: (hasError: boolean) => void;
}

const addBtnSx = {
  textTransform: 'none' as const,
  fontFamily: 'Jost, sans-serif',
  fontSize: 12.5,
  color: NAVY,
  borderRadius: RADIUS_ITEM,
  '&:hover': { bgcolor: alpha(NAVY, 0.05) },
};

const ConversationThread: FC<ConversationThreadProps> = ({
  turns,
  rawOpen,
  regeneratingUid,
  showComposer,
  chatSending,
  sendDisabled,
  onSendChat,
  onUpdateTurn,
  onDeleteTurn,
  onAddTurn,
  onRegenerate,
  onToggleRaw,
  onSetTurns,
  onConversationError,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
      <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 13, color: NAVY }}>
        Conversación
      </Typography>
      <Tooltip title={rawOpen ? 'Editor estructurado' : 'Editar JSON crudo'}>
        <ToggleButton
          value="raw"
          selected={rawOpen}
          onChange={() => onToggleRaw(!rawOpen)}
          size="small"
          sx={{
            border: 'none',
            borderRadius: RADIUS_ITEM,
            px: 1,
            py: 0.4,
            color: alpha(NAVY, 0.6),
            '&.Mui-selected': { bgcolor: alpha(NAVY, 0.08), color: NAVY },
          }}
        >
          <CodeOutlined sx={{ fontSize: 16, mr: 0.5 }} />
          <Typography sx={{ fontSize: 11.5, fontFamily: 'Jost, sans-serif', fontWeight: 600 }}>JSON</Typography>
        </ToggleButton>
      </Tooltip>
    </Box>

    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
      {rawOpen ? (
        <RawJsonPanel<PlaygroundTurn[]>
          value={turnsToRawJson(turns)}
          parse={rawJsonToTurns}
          onParsed={onSetTurns}
          onError={onConversationError}
          minRows={12}
          ariaLabel="Conversación en JSON"
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {turns.length === 0 && (
            <Typography
              sx={{
                fontSize: 12.5,
                color: alpha(NAVY, 0.45),
                fontFamily: 'Jost, sans-serif',
                textAlign: 'center',
                py: 2,
              }}
            >
              Conversación vacía. Agrega un turno para empezar a simular.
            </Typography>
          )}
          {turns.map((turn, index) => (
            <TurnBubble
              key={turn.uid}
              turn={turn}
              index={index}
              onUpdate={(patch) => onUpdateTurn(turn.uid, patch)}
              onDelete={() => onDeleteTurn(turn.uid)}
              onRegenerate={() => onRegenerate(turn.uid)}
              regenerating={regeneratingUid === turn.uid}
            />
          ))}
        </Box>
      )}
    </Box>

    {!rawOpen && (
      <Box sx={{ display: 'flex', gap: 0.5, pt: 1, flexShrink: 0 }}>
        <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => onAddTurn('user')} sx={addBtnSx}>
          Usuario
        </Button>
        <Button
          size="small"
          startIcon={<Add sx={{ fontSize: 16 }} />}
          onClick={() => onAddTurn('assistant')}
          sx={addBtnSx}
        >
          Asistente
        </Button>
        <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={() => onAddTurn('tool')} sx={addBtnSx}>
          Tool
        </Button>
      </Box>
    )}

    {!rawOpen && showComposer && <Composer onSend={onSendChat} sending={chatSending} disabled={sendDisabled} />}
  </Box>
);

export default ConversationThread;
